<template>
  <div id="billingPage">
    <b-card>
      <div class="d-flex align-items-center mb-1">
        <feather-icon icon="FileTextIcon" size="22" class="mr-1 text-primary" />
        <h4 class="mb-0">Billing details</h4>
      </div>
      <p class="text-muted small mb-2">
        These details appear on every invoice we issue you. Choose
        <strong>Individual</strong> for personal invoices, or <strong>Business</strong>
        for company invoices.
      </p>
      <p class="text-muted small mb-2">
        For businesses, enter your <strong>tax/VAT number</strong> (CUI in RO,
        Steuernummer in DE, EIN in US, etc.). For EU companies, if it's a
        registered VAT ID we verify it live via VIES — valid VAT ID outside
        Romania means <strong>0% reverse charge</strong> on the invoice. The
        amount you pay is the same regardless.
      </p>

      <BillingDetailsForm ref="billingForm" v-model="billing" />

      <div class="mt-2">
        <b-button
          variant="primary"
          :disabled="saving"
          @click="save"
        >
          <b-spinner v-if="saving" small class="mr-50" />
          Save billing details
        </b-button>
      </div>
    </b-card>
  </div>
</template>

<script>
import { BCard, BButton, BSpinner } from "bootstrap-vue";
import BillingDetailsForm from "@/components/util/BillingDetailsForm.vue";
import Billing from "@/services/billing.service.js";
import { countryByIso } from "@/libs/country-by-iso.js";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import { mapState } from "vuex";

const empty = () => ({
  account_type: "personal",
  full_name: "",
  company_name: "",
  tax_id: "",
  country: "",
  address: "",
  // Romania only — structured locality/county (ANAF e-Factura via SmartBill)
  city: "",
  county: "",
});

export default {
  name: "BillingPage",
  components: {
    BCard,
    BButton,
    BSpinner,
    BillingDetailsForm,
    ToastificationContent,
  },
  data() {
    return {
      saving: false,
      billing: empty(),
    };
  },
  computed: {
    ...mapState(["user"]),
  },
  created() {
    // Hydrate before BillingDetailsForm mounts so its data() picks up values
    this.hydrate();
  },
  async mounted() {
    // If country still empty after hydration (new user / never set billing),
    // ask backend for Cloudflare's CF-IPCountry value and pre-fill.
    if (!this.billing.country) {
      const code = await Billing.detectIpCountry();
      const name = countryByIso(code);
      if (name) this.billing = { ...this.billing, country: name };
    }
  },
  methods: {
    hydrate() {
      const u = (this.user && this.user.user) || {};
      this.billing = {
        account_type: u.billing_default_type || "personal",
        full_name: u.billing_full_name || u.name || "",
        company_name: u.billing_company_name || "",
        // Prefer tax_id; fall back to vat_id for users who saved before the merge
        tax_id: u.billing_tax_id || u.billing_vat_id || "",
        country: u.billing_country || u.country || "",
        address: u.billing_address || "",
        city: u.billing_city || "",
        county: u.billing_county || "",
      };
    },
    async save() {
      const ok = await this.$refs.billingForm.validate();
      if (!ok) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Please complete the form",
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }
      this.saving = true;
      try {
        await Billing.updateBilling(this.billing);
        // Refresh local Vuex copy so VAT preview elsewhere reflects new data
        this.$store.commit("SET_BILLING_DATA", {
          billing_default_type: this.billing.account_type,
          billing_full_name: this.billing.full_name,
          billing_company_name: this.billing.company_name,
          billing_tax_id: this.billing.tax_id,
          // vat_id is the same as tax_id when VIES validated, else null
          billing_vat_id: this.billing.vies_valid ? this.billing.tax_id : null,
          billing_vat_id_valid: this.billing.vies_valid ? 1 : 0,
          billing_country: this.billing.country,
          billing_country_code: this.billing.country_code,
          billing_address: this.billing.address,
          billing_city: this.billing.city || null,
          billing_county: this.billing.county || null,
        });
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Billing details saved",
            icon: "CheckIcon",
            variant: "success",
          },
        });
      } catch (e) {
        const msg =
          e?.response?.data?.message ||
          (e?.response?.data?.errors
            ? Object.values(e.response.data.errors)[0][0]
            : "Could not save — please try again");
        this.$toast({
          component: ToastificationContent,
          props: {
            title: "Save failed",
            icon: "XIcon",
            text: msg,
            variant: "danger",
          },
        });
      }
      this.saving = false;
    },
  },
};
</script>

<style lang="scss" scoped>
#billingPage {
  max-width: 720px;
}
</style>
